<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useRaffleApi } from '../services';
import QRCode from 'qrcode';

const props = defineProps<{
  show: boolean;
}>();

const emit = defineEmits(['close']);
const api = useRaffleApi();

// Flow State
const step = ref<'EMAIL' | 'OTP' | 'RESULTS'>('EMAIL');
const email = ref('');
const otpCode = ref('');
const loading = ref(false);
const error = ref('');
const results = ref<any[]>([]);
const participantData = ref<any>(null);

// QR View State
const showQrModal = ref(false);
const selectedSale = ref<any>(null);
const qrImage = ref('');

// Step 1: Request OTP
const handleRequestOtp = async () => {
  if (!email.value.includes('@')) {
    error.value = 'Ingresa un correo válido';
    return;
  }
  
  loading.value = true;
  error.value = '';
  
  try {
    const res = await api.requestOtp(email.value);
    if (res.debug_code) {
        console.log('DEBUG CODE:', res.debug_code); // For Dev Use
    }
    step.value = 'OTP';
  } catch (e: any) {
    console.error(e);
    error.value = e.message || "Error al enviar código. Intenta nuevamente.";
  } finally {
    loading.value = false;
  }
};

// Step 2: Verify OTP
const handleVerifyOtp = async () => {
  if (otpCode.value.length < 6) return;
  
  loading.value = true;
  error.value = '';
  
  try {
    const verifyRes = await api.verifyOtp(email.value, otpCode.value);
    const token = verifyRes.token;
    
    // Fetch Data
    const dataRes = await api.getMyTickets(token);
    
    if (dataRes.orders && dataRes.orders.length > 0) {
        results.value = dataRes.orders;
        participantData.value = dataRes.participant;
        step.value = 'RESULTS';
    } else {
        // No orders found or empty
         results.value = [];
         step.value = 'RESULTS'; // Show empty state
    }

  } catch (e: any) {
    console.error(e);
    error.value = "Código inválido o expirado.";
  } finally {
    loading.value = false;
  }
};

const openQr = async (sale: any) => {
    if (!sale.sale_id) return; // Need ID
    // Note: The new API returns sale_id, raffle_title, tickets (array)
    // We might not have qr_signature here if not returned by backend. 
    // Backend `getMyTickets` needs to return signature if we want QR. 
    // Assuming backend returns it or we generate a validation link.
    // Let's assume for now we just show a "Validado" message or similar.
    
    // TODO: Add qr_signature to MyTickets endpoint response if needed for secure validation
    
    // For now, let's just generate QR of the Sale ID
    const baseUrl = window.location.origin;
    const payload = `${baseUrl}/?verify=${sale.sale_id}`;
    
    qrImage.value = await QRCode.toDataURL(payload, { 
        width: 300,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' }
    });
    showQrModal.value = true;
};

// Reset on open
watch(() => props.show, (val) => {
    if (val) {
        step.value = 'EMAIL';
        email.value = '';
        otpCode.value = '';
        results.value = [];
        error.value = '';
        showQrModal.value = false;
    }
});
</script>

<template>
  <div v-if="show" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
    <!-- Backdrop -->
    <div class="absolute inset-0 bg-black/90 backdrop-blur-xl animate-in fade-in duration-300" @click="emit('close')"></div>

    <!-- Main Content -->
    <div class="relative z-10 w-full max-w-lg bg-surface border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-300">
      
      <!-- Header -->
      <div class="p-6 border-b border-white/5 bg-white/5 flex justify-between items-center">
        <h2 class="text-xl font-bold text-white uppercase tracking-wider">
            {{ step === 'RESULTS' ? 'Mis Tickets' : 'Consultar Tickets' }}
        </h2>
        <button @click="emit('close')" class="text-gray-400 hover:text-white transition">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
        </button>
      </div>

      <!-- STEP 1: EMAIL INPUT -->
      <div v-if="step === 'EMAIL'" class="p-8 flex flex-col gap-6">
         <div class="text-center">
             <div class="w-16 h-16 bg-gold-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-gold-500/20 text-3xl">📧</div>
             <p class="text-gray-300">Ingresa tu correo electrónico para recibir un código de acceso seguro.</p>
         </div>
         
         <div>
            <input 
                v-model="email" 
                @keyup.enter="handleRequestOtp"
                type="email" 
                placeholder="tu@correo.com" 
                class="w-full bg-black/30 border border-white/20 rounded-xl px-4 py-4 text-white text-center text-lg focus:border-gold-500 focus:outline-none transition-colors"
                autofocus
            />
            <p v-if="error" class="text-red-400 text-sm mt-2 text-center">{{ error }}</p>
         </div>

         <button 
            @click="handleRequestOtp"
            :disabled="loading || !email.includes('@')"
            class="w-full py-4 bg-gold-500 hover:bg-gold-400 disabled:opacity-50 text-black font-bold rounded-xl transition-all shadow-lg text-lg"
        >
            <span v-if="loading" class="animate-spin block w-5 h-5 border-2 border-black border-t-transparent rounded-full mx-auto"></span>
            <span v-else>ENVIAR CÓDIGO</span>
        </button>
      </div>

      <!-- STEP 2: OTP INPUT -->
      <div v-else-if="step === 'OTP'" class="p-8 flex flex-col gap-6">
         <div class="text-center">
             <div class="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-500/20 text-3xl">🔐</div>
             <p class="text-gray-300">Ingresa el código de 6 dígitos enviado a <b>{{ email }}</b></p>
         </div>
         
         <div>
            <input 
                v-model="otpCode" 
                @keyup.enter="handleVerifyOtp"
                type="text" 
                placeholder="000000" 
                maxlength="6"
                class="w-full bg-black/30 border border-white/20 rounded-xl px-4 py-4 text-white text-center text-3xl font-mono tracking-widest focus:border-gold-500 focus:outline-none transition-colors"
                autofocus
            />
            <p v-if="error" class="text-red-400 text-sm mt-2 text-center">{{ error }}</p>
         </div>

         <button 
            @click="handleVerifyOtp"
            :disabled="loading || otpCode.length < 6"
            class="w-full py-4 bg-gold-500 hover:bg-gold-400 disabled:opacity-50 text-black font-bold rounded-xl transition-all shadow-lg text-lg"
        >
            <span v-if="loading" class="animate-spin block w-5 h-5 border-2 border-black border-t-transparent rounded-full mx-auto"></span>
            <span v-else>VERIFICAR</span>
        </button>
        
        <button @click="step = 'EMAIL'" class="text-gray-500 hover:text-gray-300 text-sm underline">Cambiar correo</button>
      </div>

      <!-- STEP 3: RESULTS -->
      <div v-else-if="step === 'RESULTS'" class="flex-1 overflow-y-auto p-4 space-y-3 bg-surface min-h-[400px]">
        
        <div v-if="participantData" class="text-center mb-6 p-4 bg-white/5 rounded-xl border border-white/5">
             <p class="text-gray-400 text-xs uppercase font-bold">Bienvenido(a)</p>
             <h3 class="text-xl font-bold text-white">{{ participantData.name }}</h3>
             <p class="text-gold-500 text-sm">{{ participantData.email }}</p>
        </div>

        <div v-if="results.length > 0" class="space-y-4">
            <div v-for="order in results" :key="order.sale_id" class="p-4 bg-black/30 border border-white/10 rounded-xl hover:bg-black/40 transition-colors group">
                
                <div class="flex justify-between items-start mb-3 border-b border-white/5 pb-3">
                    <div>
                        <div class="text-[10px] text-gray-500 uppercase font-bold text-gold-400">{{ order.raffle_title }}</div>
                        <div class="text-xs text-gray-400">Comprado el {{ new Date(order.created_at * 1000).toLocaleDateString() }}</div>
                    </div>
                     <!-- Status Badge -->
                    <div class="text-right">
                         <span v-if="order.status === 'APPROVED'" class="inline-flex items-center gap-1 bg-green-500/10 text-green-400 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border border-green-500/20">
                            ✅ Validado
                         </span>
                         <span v-else class="inline-flex items-center gap-1 bg-yellow-500/10 text-yellow-400 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border border-yellow-500/20">
                            ⏳ Pendiente
                         </span>
                    </div>
                </div>

                <!-- Tickets -->
                <div class="bg-surface/50 p-3 rounded-lg border border-white/5 mb-2">
                    <p class="text-[10px] text-gray-500 uppercase font-bold mb-1">Tus Números ({{ order.tickets.length }})</p>
                    <div class="flex flex-wrap gap-2">
                        <span v-for="t in order.tickets" :key="t" class="font-mono font-bold text-white bg-white/10 px-2 py-1 rounded text-sm">
                            {{ String(t).padStart(4,'0') }}
                        </span>
                    </div>
                </div>

                <!-- Action -->
                <button 
                    @click="openQr(order)"
                    class="w-full py-2 bg-white/5 hover:bg-white/10 text-gray-300 font-bold uppercase text-[10px] tracking-widest rounded-lg transition border border-white/10 flex items-center justify-center gap-2"
                >
                    <span>📱</span> Ver Comprobante
                </button>
            </div>
        </div>

        <div v-else class="flex flex-col items-center justify-center py-12 text-gray-600 opacity-50">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <p>No tienes tickets activos asociado a este correo.</p>
        </div>
      </div>
    </div>

    <!-- QR Modal Overlay -->
    <div v-if="showQrModal" class="absolute inset-0 z-50 flex items-center justify-center p-4">
         <div class="absolute inset-0 bg-black/80 backdrop-blur-md" @click="showQrModal = false"></div>
         <div class="relative bg-white rounded-2xl p-6 w-full max-w-sm text-center shadow-2xl animate-in zoom-in-90 duration-300">
             <div class="mb-4">
                 <h3 class="text-xl font-black text-black uppercase">Ticket Digital</h3>
                 <p class="text-xs text-gray-500">Presenta este código para reclamar premios.</p>
             </div>
             
             <div class="bg-white p-2 rounded-xl border-2 border-black/10 inline-block shadow-inner mb-4">
                 <img :src="qrImage" alt="QR de Seguridad" class="w-64 h-64 object-contain" />
             </div>
             
             <button @click="showQrModal = false" class="w-full py-3 bg-black text-white font-bold rounded-xl uppercase hover:bg-gray-800 transition">
                 Cerrar
             </button>
         </div>
    </div>

  </div>
</template>
